import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../../components/Header';
import { PlatformIcon } from '../../components/shared/PlatformIcon';

type Inbox = {
  id: string;
  provider: string;
  platform: string;
  title?: string | null;
  contactName?: string | null;
  contactHandle?: string | null;
  externalAccountId?: string | null;
  metadata?: Record<string, unknown> | null;
};

type OfficialInboxStatus = {
  connectionId: string;
  platform: string;
  accountName: string;
  groupName: string;
  platformAccountId: string | null;
  needsAccountIdResolution: boolean;
  inboxId: string | null;
  deletedFromChat: boolean;
  hasInbox: boolean;
};

type ShopeeConnection = {
  id: string;
  shopId: number;
  shopName?: string | null;
  connectionName?: string | null;
  displayName?: string;
  externalId?: string;
};

type MercadoLivreConnection = {
  id: string;
  userId: number;
  nickname?: string | null;
  connectionName?: string | null;
  displayName?: string;
  externalId?: string;
};

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function crmChatRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/crm-chat${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string>) || {})
    } as HeadersInit
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.message || payload.error || `HTTP ${response.status}`);
  return payload.data;
}

function providerLabel(provider: string) {
  if (provider === 'waha') return 'WhatsApp Nao Oficial';
  if (provider === 'official') return 'WhatsApp Oficial';
  return provider;
}

function platformLabel(platform: string) {
  const labels: Record<string, string> = {
    whatsapp: 'WhatsApp', instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', twitter: 'X/Twitter',
    tiktok: 'TikTok', telegram: 'Telegram', bluesky: 'Bluesky', reddit: 'Reddit', linkedin: 'LinkedIn', threads: 'Threads',
    mercadolivre: 'Mercado Livre', shopee: 'Shopee'
  };
  return labels[platform] || platform;
}

function inboxName(inbox: Inbox) {
  return inbox.title || inbox.contactName || inbox.contactHandle || inbox.externalAccountId || `${platformLabel(inbox.platform)} ${providerLabel(inbox.provider)}`;
}

function shopeeConnectionName(connection?: ShopeeConnection | null) {
  if (!connection) return '';
  return connection.connectionName?.trim() || connection.displayName?.trim() || connection.shopName?.trim() || `Shopee ${connection.shopId}`;
}

function mercadoLivreConnectionName(connection?: MercadoLivreConnection | null) {
  if (!connection) return '';
  return connection.connectionName?.trim() || connection.displayName?.trim() || connection.nickname?.trim() || `Mercado Livre ${connection.userId}`;
}

function isVisible(inbox: Inbox) {
  return inbox.metadata?.visibleInChat !== false && inbox.metadata?.visibleInChat !== 'false';
}

function isAgentSignatureEnabled(inbox: Inbox) {
  return inbox.metadata?.agentSignatureEnabled === true || inbox.metadata?.agentSignatureEnabled === 'true';
}

function isSoundEnabled(inbox: Inbox) {
  return inbox.metadata?.soundNotification === true || inbox.metadata?.soundNotification === 'true';
}

function playPreviewBeep() {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    osc.start();
    osc.stop(ctx.currentTime + 0.34);
    osc.onended = () => ctx.close().catch(() => undefined);
  } catch {
    // sem audio disponivel
  }
}

function Toggle({ checked, disabled, onClick }: { checked: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-emerald-600' : 'bg-gray-300'} ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90'}`}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

export function ChatSettingsPage() {
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [shopeeConnections, setShopeeConnections] = useState<ShopeeConnection[]>([]);
  const [mercadoLivreConnections, setMercadoLivreConnections] = useState<MercadoLivreConnection[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [pollingShopeeConnectionId, setPollingShopeeConnectionId] = useState<string | null>(null);
  const [shopeeImportProgress, setShopeeImportProgress] = useState<Record<string, { processed: number; inserted: number; skippedReplied: number; status: string }>>({});
  const [officialStatus, setOfficialStatus] = useState<OfficialInboxStatus[]>([]);
  const [creatingInboxes, setCreatingInboxes] = useState(false);

  const missingInboxes = officialStatus.filter((item) => !item.hasInbox);

  const shopeeConnectionByShopId = new Map(shopeeConnections.map((connection) => [String(connection.shopId), connection]));
  const mercadoLivreConnectionByUserId = new Map(mercadoLivreConnections.map((connection) => [String(connection.userId), connection]));

  const connectionNameForInbox = (inbox: Inbox) => {
    if (inbox.provider === 'marketplace-hub' && inbox.platform === 'shopee') {
      const connection = shopeeConnectionByShopId.get(String(inbox.externalAccountId || ''));
      return shopeeConnectionName(connection);
    }
    if (inbox.provider === 'marketplace-hub' && inbox.platform === 'mercadolivre') {
      const connection = mercadoLivreConnectionByUserId.get(String(inbox.externalAccountId || ''));
      return mercadoLivreConnectionName(connection);
    }
    return '';
  };

  const loadInboxes = async () => {
    setLoading(true);
    try {
      const [rows, shopeeRows, mercadoLivreRows] = await Promise.all([
        crmChatRequest<Inbox[]>('/inboxes'),
        fetch('/api/shopee/connections', { headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit })
          .then((response) => response.ok ? response.json() : { data: [] })
          .then((payload) => payload.success ? (payload.data || []) : [])
          .catch(() => []),
        fetch('/api/mercado-livre/connections', { headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit })
          .then((response) => response.ok ? response.json() : { data: [] })
          .then((payload) => payload.success ? (payload.data || []) : [])
          .catch(() => [])
      ]);
      setInboxes(rows);
      setShopeeConnections(shopeeRows);
      setMercadoLivreConnections(mercadoLivreRows);
      setNames(Object.fromEntries(rows.map((inbox) => [inbox.id, inboxName(inbox)])));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar caixas');
    } finally {
      setLoading(false);
    }
  };

  const pollAllShopeeComments = async (inbox: Inbox) => {
    const connection = shopeeConnectionByShopId.get(String(inbox.externalAccountId || ''));
    if (!connection?.id || pollingShopeeConnectionId) return;
    const currentStatus = shopeeImportProgress[connection.id]?.status;
    if (currentStatus !== 'paused' && !window.confirm(`Buscar o historico de comentarios da loja "${shopeeConnectionName(connection)}" aos poucos? Comentarios ja respondidos na Shopee serao ignorados.`)) return;

    setPollingShopeeConnectionId(connection.id);
    try {
      const response = await fetch(`/api/shopee/connections/${connection.id}/comments/import/start`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit,
        body: JSON.stringify({ pageSize: 50, intervalMs: 10000 })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) throw new Error(payload.message || payload.error || 'Erro ao iniciar importacao');
      const result = payload.data || {};
      setShopeeImportProgress((current) => ({ ...current, [connection.id]: result }));
      toast.success(currentStatus === 'paused' ? 'Importacao retomada no backend' : 'Importacao iniciada no backend');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao iniciar importacao da Shopee');
    } finally {
      setPollingShopeeConnectionId(null);
    }
  };

  const stopShopeeCommentsImport = async (connectionId: string) => {
    try {
      const response = await fetch(`/api/shopee/connections/${connectionId}/comments/import/stop`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) throw new Error(payload.message || payload.error || 'Erro ao parar importacao');
      setShopeeImportProgress((current) => ({ ...current, [connectionId]: payload.data || current[connectionId] }));
      toast.success('Importacao pausada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao parar importacao da Shopee');
    }
  };

  const refreshShopeeImportStatuses = async (connections: ShopeeConnection[] = shopeeConnections) => {
    const statuses = await Promise.all(connections.map(async (connection) => {
      try {
        const response = await fetch(`/api/shopee/connections/${connection.id}/comments/import/status`, {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit
        });
        const payload = await response.json().catch(() => ({}));
        return response.ok && payload.success ? [connection.id, payload.data] : null;
      } catch {
        return null;
      }
    }));
    setShopeeImportProgress((current) => ({ ...current, ...Object.fromEntries(statuses.filter(Boolean) as [string, any][]) }));
  };

  // Conexões oficiais (Zernio) × existe caixa no Chat CRM. Serve para o caso em
  // que a conexão foi criada por um caminho que não gravou o accountId: a caixa
  // nunca é criada e, sem caixa, os webhooks de entrada são descartados.
  const loadOfficialInboxStatus = async () => {
    try {
      const rows = await crmChatRequest<OfficialInboxStatus[]>('/official-inbox-status');
      setOfficialStatus(rows || []);
    } catch {
      setOfficialStatus([]);
    }
  };

  const createMissingInboxes = async () => {
    setCreatingInboxes(true);
    try {
      const result = await crmChatRequest<{
        official: { createdOrUpdated: number; skipped: number; errors: number };
        repair?: { repaired: number; alreadyOk: number; unresolved: number } | null;
      }>('/backfill-inboxes', { method: 'POST' });
      const created = result?.official?.createdOrUpdated ?? 0;
      const errors = result?.official?.errors ?? 0;
      const repaired = result?.repair?.repaired ?? 0;
      const unresolved = result?.repair?.unresolved ?? 0;
      const parts = [`${created} caixa(s) criada(s)/atualizada(s)`];
      if (repaired > 0) parts.push(`${repaired} conversa(s) com id corrigido`);
      if (unresolved > 0) parts.push(`${unresolved} conversa(s) sem correspondência na Zernio`);
      if (errors > 0) parts.push(`${errors} com erro — veja os logs`);
      toast.success(`${parts.join(', ')}.`);
      await Promise.all([loadInboxes(), loadOfficialInboxStatus()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar caixas');
    } finally {
      setCreatingInboxes(false);
    }
  };

  useEffect(() => { loadInboxes(); loadOfficialInboxStatus(); }, []);

  useEffect(() => {
    if (!shopeeConnections.length) return;
    refreshShopeeImportStatuses(shopeeConnections);
    const timer = window.setInterval(() => refreshShopeeImportStatuses(shopeeConnections), 3000);
    return () => window.clearInterval(timer);
  }, [shopeeConnections]);

  const updateInbox = async (inbox: Inbox, patch: { title?: string; visibleInChat?: boolean; soundNotification?: boolean; agentSignatureEnabled?: boolean }) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/chat-settings`, {
        method: 'PATCH',
        body: JSON.stringify(patch)
      });
      setInboxes((items) => items.map((item) => item.id === inbox.id ? updated : item));
      setNames((current) => ({ ...current, [inbox.id]: inboxName(updated) }));
      toast.success('Caixa atualizada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar caixa');
    } finally {
      setSavingId(null);
    }
  };

  const deleteInbox = async (inbox: Inbox) => {
    const ok = window.confirm(`Deletar a caixa "${inboxName(inbox)}" do Chat CRM? Isso remove tambem as conversas e mensagens salvas dessa caixa no CRM Chat. A conexao original continua existindo para outras funcionalidades.`);
    if (!ok) return;
    setSavingId(inbox.id);
    try {
      await crmChatRequest(`/inboxes/${inbox.id}`, { method: 'DELETE' });
      setInboxes((items) => items.filter((item) => item.id !== inbox.id));
      toast.success('Caixa removida do Chat CRM');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao deletar caixa');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <Header title="Configuracoes do Chat" subtitle="Controle quais caixas aparecem e recebem mensagens no CRM Chat." />
      <div className="p-6 space-y-4">
        {/* Conexões oficiais sem caixa no Chat CRM. Sem caixa, os webhooks de
            entrada da Zernio são descartados — nada chega e nada é respondido. */}
        {officialStatus.length > 0 && (
          <div className={`rounded-lg border p-4 ${missingInboxes.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Caixas das conexões oficiais</div>
                {missingInboxes.length > 0 ? (
                  <p className="mt-1 text-xs text-amber-700">
                    {missingInboxes.length} conexão(ões) ativa(s) sem caixa no Chat CRM. Enquanto a caixa não existir,
                    as mensagens recebidas por essa conta são descartadas e não é possível responder por aqui.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Todas as {officialStatus.length} conexão(ões) oficiais já têm caixa no Chat CRM.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={createMissingInboxes}
                disabled={creatingInboxes}
                className={`shrink-0 rounded-md px-3 py-2 text-xs font-medium text-white disabled:opacity-50 ${
                  missingInboxes.length > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {creatingInboxes
                  ? 'Criando...'
                  : missingInboxes.length > 0 ? 'Criar caixas faltantes' : 'Revalidar caixas'}
              </button>
            </div>

            {missingInboxes.length > 0 && (
              <ul className="mt-3 space-y-1">
                {missingInboxes.map((item) => (
                  <li key={item.connectionId} className="flex items-center gap-2 text-xs text-amber-800">
                    <PlatformIcon platform={item.platform} size={14} />
                    <span className="font-medium">{item.accountName}</span>
                    {item.groupName && <span className="text-amber-600">· {item.groupName}</span>}
                    {item.deletedFromChat && (
                      <span className="rounded bg-red-200 px-1.5 py-0.5 text-[10px] text-red-900">
                        removida do chat — mensagens estão sendo descartadas
                      </span>
                    )}
                    {item.needsAccountIdResolution && (
                      <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] text-amber-900">
                        conta será identificada na Zernio
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">Carregando caixas...</div>
        ) : inboxes.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">Nenhuma caixa de entrada conectada ao Chat CRM.</div>
        ) : inboxes.map((inbox) => (
          <div key={inbox.id} className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-700">
                  <PlatformIcon platform={inbox.provider === 'waha' ? 'whatsapp' : inbox.platform} size={18} />
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">{inboxName(inbox)}</div>
                  <div className="mt-0.5 text-xs text-gray-500">{platformLabel(inbox.platform)} / {providerLabel(inbox.provider)}</div>
                  {inbox.provider === 'marketplace-hub' && inbox.platform === 'shopee' && (
                    <div className="mt-0.5 text-xs text-gray-500">Conexao Shopee: {connectionNameForInbox(inbox) || 'Nao identificada'}{inbox.externalAccountId ? ` - Shop ID ${inbox.externalAccountId}` : ''}</div>
                  )}
                  {inbox.provider === 'marketplace-hub' && inbox.platform === 'mercadolivre' && (
                    <div className="mt-0.5 text-xs text-gray-500">Conexao Mercado Livre: {connectionNameForInbox(inbox) || 'Nao identificada'}{inbox.externalAccountId ? ` - User ID ${inbox.externalAccountId}` : ''}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => deleteInbox(inbox)}
                disabled={savingId === inbox.id}
                className="rounded-md border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                Deletar caixa
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Nome exibido</span>
                <input
                  value={names[inbox.id] || ''}
                  onChange={(event) => setNames((current) => ({ ...current, [inbox.id]: event.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </label>
              <button
                onClick={() => updateInbox(inbox, { title: names[inbox.id] || inboxName(inbox) })}
                disabled={savingId === inbox.id}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                Salvar nome
              </button>
            </div>

            {inbox.provider === 'marketplace-hub' && inbox.platform === 'shopee' && (
              <div className="mt-4 flex items-start justify-between gap-4 rounded-md bg-orange-50 px-3 py-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">Comentarios de avaliacao Shopee</div>
                  <div className="mt-0.5 text-xs text-gray-500">O polling automatico busca comentarios do dia e novos comentarios. A importacao manual roda em paginas, com pausa entre chamadas, e ignora comentarios ja respondidos na Shopee.</div>
                  {(() => {
                    const connectionId = shopeeConnectionByShopId.get(String(inbox.externalAccountId || ''))?.id || '';
                    const progress = shopeeImportProgress[connectionId];
                    if (!progress) return null;
                    const statusLabel = progress.status === 'running' ? 'rodando' : progress.status === 'paused' ? 'pausada' : progress.status === 'completed' ? 'concluida' : progress.status === 'failed' ? 'erro' : 'parada';
                    return <div className="mt-1 text-xs text-orange-800">Importacao {statusLabel}: {progress.processed} novos verificados, {progress.inserted} importados, {progress.skippedReplied} ja respondidos ignorados</div>;
                  })()}
                </div>
                {(() => {
                  const connectionId = shopeeConnectionByShopId.get(String(inbox.externalAccountId || ''))?.id || '';
                  const status = shopeeImportProgress[connectionId]?.status || 'idle';
                  const running = status === 'running';
                  const paused = status === 'paused';
                  const completed = status === 'completed' && Number(shopeeImportProgress[connectionId]?.itemCount || 0) > 0;
                  return running ? (
                    <button
                      type="button"
                      onClick={() => stopShopeeCommentsImport(connectionId)}
                      className="rounded-md border border-red-300 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50"
                    >
                      Parar importacao
                    </button>
                  ) : completed ? (
                    <button
                      type="button"
                      disabled
                      className="rounded-md border border-green-300 bg-white px-3 py-2 text-xs font-medium text-green-700 opacity-80"
                    >
                      Importacao concluida
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => pollAllShopeeComments(inbox)}
                      disabled={Boolean(pollingShopeeConnectionId)}
                      className="rounded-md border border-orange-300 bg-white px-3 py-2 text-xs font-medium text-orange-800 hover:bg-orange-100 disabled:cursor-wait disabled:opacity-60"
                    >
                      {paused ? 'Retomar importacao' : status === 'completed' ? 'Buscar por produtos' : 'Buscar todos comentarios'}
                    </button>
                  );
                })()}
              </div>
            )}

            <div className="mt-4 flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-3">
              <div>
                <div className="text-sm font-medium text-gray-900">Visivel no Chat CRM</div>
                <div className="mt-0.5 text-xs text-gray-500">Quando desativado, a caixa e suas conversas ficam escondidas da tela do chat, mas a conexao continua ativa.</div>
              </div>
              <Toggle checked={isVisible(inbox)} disabled={savingId === inbox.id} onClick={() => updateInbox(inbox, { visibleInChat: !isVisible(inbox) })} />
            </div>

            <div className="mt-3 flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-3">
              <div>
                <div className="text-sm font-medium text-gray-900">Aviso sonoro de novas mensagens</div>
                <div className="mt-0.5 text-xs text-gray-500">Toca um som no navegador quando chega uma nova mensagem nesta caixa enquanto o Chat CRM esta aberto.</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={playPreviewBeep}
                  className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  Testar som
                </button>
                <Toggle checked={isSoundEnabled(inbox)} disabled={savingId === inbox.id} onClick={() => updateInbox(inbox, { soundNotification: !isSoundEnabled(inbox) })} />
              </div>
            </div>

            <div className="mt-3 flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-3">
              <div>
                <div className="text-sm font-medium text-gray-900">Assinatura do atendente</div>
                <div className="mt-0.5 text-xs text-gray-500">Toda mensagem enviada por um usuario nesta caixa sai com o nome dele no topo (ex.: <span className="font-semibold">*Maria*:</span> e a mensagem na linha de baixo). Respostas da IA e agendamentos nao sao assinados.</div>
              </div>
              <Toggle checked={isAgentSignatureEnabled(inbox)} disabled={savingId === inbox.id} onClick={() => updateInbox(inbox, { agentSignatureEnabled: !isAgentSignatureEnabled(inbox) })} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
